import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, firstValueFrom } from 'rxjs';

export type WebcamStatus =
  | 'idle'
  | 'starting'
  | 'ready'
  | 'reconnecting'
  | 'stopped'
  | 'error';

export interface WebcamStartOptions {
  deviceId?: string; // Exact device to use
  preferFrontCamera?: boolean; // Prefer user-facing camera when deviceId is not provided
  withAudio?: boolean; // Include microphone
  maxWidth?: number; // Soft cap for width selection
  maxHeight?: number; // Soft cap for height selection
  frameRate?: number; // Preferred frame rate
}

export interface VideoFrameData {
  captureTime: number;
  bitmap: ImageBitmap;
  width: number;
  height: number;
}

@Injectable({ providedIn: 'root' })
export class WebcamService {
  // Public state
  private statusBS = new BehaviorSubject<WebcamStatus>('idle');
  readonly status$ = this.statusBS.asObservable();

  private streamBS = new BehaviorSubject<MediaStream | null>(null);
  readonly stream$: Observable<MediaStream | null> = this.streamBS.asObservable();

  private audioStreamBS = new BehaviorSubject<MediaStream | null>(null);
  readonly audioStream$: Observable<MediaStream | null> = this.audioStreamBS.asObservable();

  private framesSubject = new Subject<VideoFrameData>();
  readonly frames$ = this.framesSubject.asObservable();

  // Internal
  private internalVideo: HTMLVideoElement | null = null; // Headless video that keeps stream alive
  private attachedVideos = new Set<HTMLVideoElement>(); // Client video elements attached to the same stream
  private currentOptions: Required<WebcamStartOptions> = {
    deviceId: '',
    preferFrontCamera: true,
    withAudio: false,
    maxWidth: 3840,
    maxHeight: 2160,
    frameRate: 30,
  };
  private broadcastFrames = false; // When true, we emit ImageBitmap frames to frames$
  private intervalId: any = null; // Fallback loop
  private deviceChangeListenerAdded = false;
  private visibilityListenerAdded = false;
  private destroyed = false;

  private get hasVideoTrack(): boolean {
    const s = this.streamBS.getValue();
    return !!s && s.getVideoTracks().length > 0;
  }
   public hint$ = new BehaviorSubject<string | null>(null);

  constructor(private zone: NgZone) {}

  // Helper: attempt to play a video element without blocking and swallow AbortError
  private safePlay(el: HTMLVideoElement | null | undefined): void {
    try {
      const p = el?.play?.();
      if (p && typeof (p as Promise<any>).catch === 'function') {
        (p as Promise<void>).catch((err: any) => {
          // Ignore AbortError which happens when srcObject changes while play() is pending
          if (err && err.name === 'AbortError') return;
          // Silently ignore NotAllowedError (autoplay blocked) and other expected errors,
          // but log unexpected ones for debugging
          if (err && err.name !== 'NotAllowedError') {
            console.warn('[WebcamService] video.play() failed', err);
          }
        });
      }
    } catch (e) {
      // Very defensive: synchronous exceptions are unlikely but safe to ignore
    }
  }

  // Core API
  async start(options?: WebcamStartOptions): Promise<MediaStream> {
    this.destroyed = false;
    this.statusBS.next(this.hasVideoTrack ? 'reconnecting' : 'starting');
    this.currentOptions = {
      ...this.currentOptions,
      ...(options || {}),
    };

    // Remember preferred deviceId when provided
    if (this.currentOptions.deviceId) {
      try {
        localStorage.setItem('webcam.deviceId', this.currentOptions.deviceId);
      } catch {}
    } else {
      // Restore last used device
      try {
        const saved = localStorage.getItem('webcam.deviceId');
        if (saved) this.currentOptions.deviceId = saved;
      } catch {}
    }

    // Stop existing
    await this.stopInternal(false);

    const stream = await this.acquireStreamWithFallback(this.currentOptions);

    // Setup internal headless video element once
    if (!this.internalVideo) {
      this.internalVideo = document.createElement('video');
      this.internalVideo.playsInline = true;
      this.internalVideo.muted = true; // avoid autoplay restrictions
      this.internalVideo.autoplay = true;
      this.internalVideo.style.position = 'fixed';
      this.internalVideo.style.opacity = '0';
      this.internalVideo.style.pointerEvents = 'none';
      this.internalVideo.style.width = '1px';
      this.internalVideo.style.height = '1px';
      // Keep it in DOM to satisfy some browsers that pause detached videos
      document.body.appendChild(this.internalVideo);
    }

    this.internalVideo.srcObject = stream;
    // Ensure playback (non-blocking)
    // Don't await play(): calling play() returns a promise which may be rejected
    // if a new load request happens; we intentionally swallow AbortError and
    // other expected rejections so that start() doesn't block or surface
    // unhandled promise rejections during app startup.
    this.safePlay(this.internalVideo);

    // Attach to any already-registered elements
    for (const el of this.attachedVideos) {
      el.srcObject = stream;
      try {
        // Don't await: let UI continue. Use safePlay to avoid unhandled rejections
        this.safePlay(el);
      } catch {}
    }

    // Start frame loop if needed
    this.startFrameLoop();

    // Track ends and reconnect if needed
    this.installResilienceHandlers();

    this.statusBS.next('ready');
    return stream;
  }

  async stop(): Promise<void> {
    await this.stopInternal(true);
  }

  private async stopInternal(markStopped: boolean): Promise<void> {
    this.stopFrameLoop();
    const stream = this.streamBS.getValue();
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    this.streamBS.next(null);
    this.audioStreamBS.next(null);
    if (this.internalVideo) {
      try {
        this.internalVideo.pause();
      } catch {}
      this.internalVideo.srcObject = null as any;
    }
    if (markStopped) this.statusBS.next('stopped');
  }

  // Expose stream synchronously when available
  getStream(): MediaStream | null {
    return this.streamBS.getValue();
  }

  // Audio only stream convenience
  getAudioStream(): MediaStream | null {
    return this.audioStreamBS.getValue();
  }

  // Attach/detach client video elements to the shared stream
  attachTo(element: HTMLVideoElement, opts?: { mirrored?: boolean; autoplay?: boolean }): void {
    this.attachedVideos.add(element);
    const stream = this.streamBS.getValue();
    if (opts?.mirrored) {
      element.style.transformOrigin = 'center';
      element.style.transform = 'scaleX(-1)';
    }
    if (stream) {
      element.srcObject = stream;
      if (opts?.autoplay !== false) {
        // Fire and forget; use safePlay() so returned promise rejections are handled
        this.safePlay(element);
      }
    }
  }

  detachFrom(element: HTMLVideoElement): void {
    if (this.attachedVideos.has(element)) {
      this.attachedVideos.delete(element);
      try {
        element.pause?.();
      } catch {}
      element.srcObject = null;
    }
  }

  // One-shot frame capture. Uses createImageBitmap for speed and zero-copy when possible
  async captureFrame(): Promise<VideoFrameData | null> {
    if (!this.internalVideo || !this.hasVideoTrack) return null;
    const v = this.internalVideo;
    try {
      const bitmap = await createImageBitmap(v);
      return {
        captureTime: performance.now(),
        bitmap,
        width: v.videoWidth,
        height: v.videoHeight,
      };
    } catch (e) {
      console.warn('[WebcamService] captureFrame failed', e);
      return null;
    }
  }

  // Enable/disable continuous frame broadcasting via frames$
  setFrameBroadcastEnabled(enabled: boolean): void {
    this.broadcastFrames = !!enabled;
  }

  // Switch to a different device
  async switchDevice(deviceId: string): Promise<void> {
    this.currentOptions.deviceId = deviceId || '';
    try {
      localStorage.setItem('webcam.deviceId', this.currentOptions.deviceId);
    } catch {}
    await this.start(this.currentOptions);
  }

  // Enumerate video input devices
  async listVideoInputs(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'videoinput');
  }

  // Internal: Acquire stream trying best constraints first, with fallbacks
  private async acquireStreamWithFallback(opts: Required<WebcamStartOptions>): Promise<MediaStream> {
    const candidates: MediaStreamConstraints[] = this.buildConstraintCandidates(opts);
    const errors: unknown[] = [];
    for (const c of candidates) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(c);
        // Split out audio stream if requested
        if (opts.withAudio && stream.getAudioTracks().length) {
          const audio = new MediaStream(stream.getAudioTracks());
          this.audioStreamBS.next(audio);
        } else {
          this.audioStreamBS.next(null);
        }
        this.streamBS.next(stream);
        return stream;
      } catch (e) {
        errors.push(e);
      }
    }
    // As last resort, try bare minimum
    try {
      const fallback = await navigator.mediaDevices.getUserMedia({ video: true, audio: opts.withAudio });
      if (opts.withAudio && fallback.getAudioTracks().length) {
        this.audioStreamBS.next(new MediaStream(fallback.getAudioTracks()));
      } else {
        this.audioStreamBS.next(null);
      }
      this.streamBS.next(fallback);
      return fallback;
    } catch (finalErr) {
      this.statusBS.next('error');
      console.error('[WebcamService] Unable to acquire camera stream', errors, finalErr);
      throw finalErr;
    }
  }

  private buildConstraintCandidates(opts: Required<WebcamStartOptions>): MediaStreamConstraints[] {
    const facingMode = opts.preferFrontCamera ? 'user' : 'environment';
    const base: any = {
      frameRate: { ideal: Math.min(Math.max(opts.frameRate, 15), 60) },
    };
    if (opts.deviceId) base.deviceId = { exact: opts.deviceId };
    else base.facingMode = { ideal: facingMode };

    // Descending quality candidates
    const sizes = [
      [3840, 2160],
      [2560, 1440],
      [1920, 1080],
      [1280, 720],
      [960, 540],
      [640, 480],
    ].filter(([w, h]) => w <= opts.maxWidth && h <= opts.maxHeight);

    const candidates: MediaStreamConstraints[] = sizes.map(([w, h]) => ({
      video: {
        ...base,
        width: { ideal: w },
        height: { ideal: h },
      },
      audio: opts.withAudio,
    }));

    // Also try without width/height to let browser pick best
    candidates.push({ video: { ...base }, audio: opts.withAudio });
    return candidates;
  }

  // Resilience: reconnect on ended tracks, visibility changes or device changes
  private installResilienceHandlers(): void {
    const stream = this.streamBS.getValue();
    if (!stream) return;

    // Track ended -> try to restart
    stream.getTracks().forEach((t) => {
      t.onended = () => {
        if (this.destroyed) return;
        this.statusBS.next('reconnecting');
        this.start(this.currentOptions).catch(() => this.statusBS.next('error'));
      };
    });

    if (!this.deviceChangeListenerAdded) {
      this.deviceChangeListenerAdded = true;
      navigator.mediaDevices.addEventListener('devicechange', () => {
        if (this.destroyed) return;
        // Keep current stream but refresh device list or re-acquire if tracks went away
        if (!this.hasVideoTrack) {
          this.statusBS.next('reconnecting');
          this.start(this.currentOptions).catch(() => this.statusBS.next('error'));
        }
      });
    }

    if (!this.visibilityListenerAdded) {
      this.visibilityListenerAdded = true;
      document.addEventListener('visibilitychange', () => {
        if (this.destroyed) return;
        if (document.visibilityState === 'visible' && !this.hasVideoTrack) {
          this.statusBS.next('reconnecting');
          this.start(this.currentOptions).catch(() => this.statusBS.next('error'));
        }
      });
    }
  }

  // Frame loop using requestVideoFrameCallback when available, falling back to setInterval
  private startFrameLoop(): void {
    if (!this.internalVideo) return;
    const v = this.internalVideo;
    const hasRVFC = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

    const loopRVFC = (now: DOMHighResTimeStamp, meta: any) => {
      if (!this.internalVideo || this.destroyed) return;
      // Schedule next asap
      (this.internalVideo as any).requestVideoFrameCallback(loopRVFC);

      if (!this.broadcastFrames) return;
      // Only emit if there are subscribers
      if ((this.framesSubject as any).observers?.length === 0) return;

      // Emit ImageBitmap for this frame
      this.zone.runOutsideAngular(async () => {
        try {
          const bitmap = await createImageBitmap(v);
          this.framesSubject.next({
            captureTime: meta?.mediaTime ? meta.mediaTime * 1000 : performance.now(),
            bitmap,
            width: v.videoWidth,
            height: v.videoHeight,
          });
        } catch {}
      });
    };

    const loopInterval = () => {
      if (!this.internalVideo || this.destroyed) return;
      if (!this.broadcastFrames) return;
      if ((this.framesSubject as any).observers?.length === 0) return;
      this.zone.runOutsideAngular(async () => {
        try {
          const bitmap = await createImageBitmap(v);
          this.framesSubject.next({
            captureTime: performance.now(),
            bitmap,
            width: v.videoWidth,
            height: v.videoHeight,
          });
        } catch {}
      });
    };

    if (hasRVFC) {
      try {
        (v as any).requestVideoFrameCallback(loopRVFC);
      } catch {
        // Fall back
        this.intervalId = setInterval(loopInterval, Math.round(1000 / this.currentOptions.frameRate));
      }
    } else {
      this.intervalId = setInterval(loopInterval, Math.round(1000 / this.currentOptions.frameRate));
    }
  }

  private stopFrameLoop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Clean up completely (optional for app shutdown)
  async destroy(): Promise<void> {
    this.destroyed = true;
    await this.stopInternal(true);
    if (this.internalVideo && this.internalVideo.parentElement) {
      try {
        this.internalVideo.parentElement.removeChild(this.internalVideo);
      } catch {}
    }
    this.internalVideo = null;
    this.attachedVideos.clear();
  }

  resetConfiguration(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.currentOptions = {
        deviceId: '',
        preferFrontCamera: true,
        withAudio: false,
        maxWidth: 3840,
        maxHeight: 2160,
        frameRate: 30,
      };
      resolve();
    });
  }
}
